import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MapMarker } from '../../../utils/mapUtils';
import { TrackingInfo } from '@evermile/customer-tracking';
import { MapComponent } from '../../../map/map/map.component';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import CustomerTrackingStatusEnum = TrackingInfo.CustomerTrackingStatusEnum;
import { AddressType } from '@evermile/evermile_internal_order_manager';
import { OrderStatus } from '@evermile/api';

const disabledMapStatuses = [
  CustomerTrackingStatusEnum.Failed,
  CustomerTrackingStatusEnum.ReturnedToSender,
  CustomerTrackingStatusEnum.ReturningToSender,
  CustomerTrackingStatusEnum.Cancelled,
];
export const defaultMapBoundsPadding = { bottom: 190, top: 50, left: 15, right: 15 };

@Component({
  selector: 'common-order-status-tracking',
  templateUrl: './order-status-tracking.component.html',
  styleUrls: ['./order-status-tracking.component.scss'],
})
export class OrderStatusTrackingComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() mapMarkers?: MapMarker[];
  @Input() contained?: boolean;
  @Input() isAdmin?: boolean;
  @Input() orderDetails?: TrackingInfo;
  @Input() status?: CustomerTrackingStatusEnum;
  @Input() merchantPhone?: string;
  @Input() merchantEmail?: string;
  @Input() merchantName?: string;
  @Input() merchantOffset!: number;
  @Input() dropoffStart?: string;
  @Input() dropoffEnd?: string;
  @Input() communicatedDropoffInfoText?: string;
  @Input() externalOrderId?: string;
  @Input() dropoffEta?: string;
  @Input() addressLine1!: string;
  @Input() shouldShowEditButtonForMerchant = false;
  @Input() addressLine2?: string;
  @Input() brandColor!: string;
  @Input() chatAllowed?: boolean;
  @Input() textColor!: string;
  @Input() shouldShowContactDriverMessage?: boolean = false;
  @Input() fullScreen = false;
  @Input({ required: true }) allowMapView?: boolean;
  @Input() nativeTrackingUrl?: string;
  @Input() showRebookButton = false;
  @Input() disableRebookButton = false;
  @Input() editCtaFontWeight?: string;
  @Output() toggleCardFullScreen = new EventEmitter();
  mapBoundsPadding = defaultMapBoundsPadding;
  @ViewChild('mapComponent') mapComponent!: MapComponent;
  @ViewChild('statusTrackingWrapper') statusTrackingWrapper!: ElementRef;
  @ViewChild('statusRelativeTrackingWrapper') statusRelativeTrackingWrapper!: ElementRef;
  @ViewChild('orderStatusContainer') orderStatusContainer!: ElementRef;
  private subMobileMode?: Subscription;
  mobileMode = false;
  enableMapView?: boolean;
  readonly StatusEnum = CustomerTrackingStatusEnum;

  constructor(private breakpointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    this.enableMapView = this.allowMapView && this.status && !disabledMapStatuses.includes(this.status);
    this.subMobileMode = this.breakpointObserver.observe(['(max-width: 1024px)']).subscribe((result) => {
      this.mobileMode = result.matches;
    });
  }

  ngAfterViewInit(): void {
    this.setMapWrappersHeight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['status'] && !changes['status'].firstChange) || changes['allowMapView']) {
      this.enableMapView = this.allowMapView && this.status && !disabledMapStatuses.includes(this.status);
      this.setMapWrappersHeight();
    }
  }

  setMapWrappersHeight() {
    if (!this.fullScreen) {
      if (this.enableMapView) {
        const statusContainerHeight = this.orderStatusContainer?.nativeElement?.clientHeight || 0;

        // the height should be at least 2 times the status container height + 70px padding
        let newMapWrapperHeight = Math.max(statusContainerHeight + 260, statusContainerHeight * 2 + 70);

        if (this.status === OrderStatus.Delivered) {
          newMapWrapperHeight = newMapWrapperHeight - 120;
        }

        this.statusTrackingWrapper?.nativeElement.setAttribute('style', `height: ${newMapWrapperHeight}px`);
        this.statusRelativeTrackingWrapper?.nativeElement.setAttribute('style', `height: ${newMapWrapperHeight}px`);
        this.mapBoundsPadding = { ...defaultMapBoundsPadding, bottom: statusContainerHeight + 20 };
      } else {
        this.statusTrackingWrapper?.nativeElement?.setAttribute('style', `height: auto`);
        this.statusRelativeTrackingWrapper?.nativeElement.setAttribute('style', `height: auto`);
      }
    }
  }

  ngOnDestroy() {
    this.subMobileMode?.unsubscribe();
  }

  protected readonly AddressType = AddressType;
  protected readonly OrderStatus = OrderStatus;
}
